import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import AppLayout from '@/components/layout/AppLayout.vue'
import AdminEmployeesView from '@/views/AdminEmployeesView.vue'
import LoginView from '@/views/LoginView.vue'
import NotFoundView from '@/views/NotFoundView.vue'
import EmployeesIcon from '@/components/icons/EmployeesIcon.vue'
import RegistrationView from '@/views/RegistrationView.vue'
import ForgotPasswordView from '@/views/ForgotPasswordView.vue'
import ProfileView from '@/views/ProfileView.vue'
import { Folders, FileText, FileCheck, LayoutDashboard } from 'lucide-vue-next'

import AdminMaterialsView from '@/views/AdminMaterialsView.vue'
import AdminOnlineFormView from '@/views/AdminOnlineFormView.vue'
import AdminCompletedFormsView from '@/views/AdminCompletedFormsView.vue'
import AdminEmployeeDocumentsView from '@/views/AdminEmployeeDocumentsView.vue'
import AdminEmployeeDashboardView from '@/views/AdminDashboardView.vue'
import EmployeeMaterialsView from '@/views/EmployeeMaterialsView.vue'
import EmployeeDocumentsView from '@/views/EmployeeDocumentsView.vue'
import EmployeeCompletedFormsView from '@/views/EmployeeCompletedFormsView.vue'

// Types
interface NavigationItem {
	name: string
	to: string
	icon: any
}

interface RouteMeta {
	requiresAuth?: boolean
	title?: string
	icon?: any
	hidden?: boolean
	roles?: string[] 
}

const LAST_ACCESSIBLE_ROUTE_KEY = 'last-accessible-route'

const getDefaultRouteNameByRole = (role?: string): string => {
	const normalizedRole = role?.toLowerCase()
	if (normalizedRole === 'admin') return 'admin-dashboard'
	if (normalizedRole === 'employee') return 'employee-dashboard'
	return 'login'
}

const saveLastAccessibleRoute = (fullPath: string) => {
	if (!fullPath || fullPath === '/') return
	localStorage.setItem(LAST_ACCESSIBLE_ROUTE_KEY, fullPath)
}

const getLastAccessibleRoute = (): string | null => {
  	return localStorage.getItem(LAST_ACCESSIBLE_ROUTE_KEY)
}

// Route Configuration
const routes: RouteRecordRaw[] = [
	{
		path: '/',
		component: AppLayout,
		meta: { requiresAuth: true },
		children: [
		{
			path: '',
			name: 'portal-home',
			redirect: () => {
				const authStore = useAuthStore()
				const lastAccessibleRoute = getLastAccessibleRoute()
				const defaultRouteName = getDefaultRouteNameByRole(authStore.user?.role)

				if (lastAccessibleRoute && lastAccessibleRoute !== '/') {
					return lastAccessibleRoute
				}

				return { name: defaultRouteName }
			},
			meta: {
				requiresAuth: true,
				hidden: true
			}
		},
		// Admin Routes
		{
			path: 'admin-dashboard',
			name: 'admin-dashboard',
			component: AdminEmployeeDashboardView,
			meta: {
				requiresAuth: true,
				title: 'Dashboard',
				icon: LayoutDashboard,
				roles: ['admin']
			}
		},
		{
			path: 'employees',
			name: 'employees',
			component: AdminEmployeesView,
			meta: {
				requiresAuth: true,
				title: 'Manage Employees',
				icon: EmployeesIcon,
				roles: ['admin']
			}
		},
		{
			path: 'admin/materials',
			name: 'materials',
			component: AdminMaterialsView,
			meta: {
				requiresAuth: true,
				roles: ['admin'],
				title: 'Manage Materials',
				icon: Folders
			}
		},
		{
			path: 'admin/materials/:id/view',
			name: 'view-online-form',
			component: AdminOnlineFormView,
			meta: {
				requiresAuth: true,
				roles: ['admin'],
				title: 'Online Form',
				hidden: true
			}
		},
		{
			path: 'employee-materials/:id/view',
			name: 'employee-view-online-form',
			component: AdminOnlineFormView,
			meta: {
				requiresAuth: true,
				roles: ['employee'],
				title: 'Online Form',
				hidden: true
			}
		},
		{
			path: 'admin/completed-forms',
			name: 'completed-forms',
			component: AdminCompletedFormsView,
			meta: {
				requiresAuth: true,
				roles: ['admin'],
				title: 'Completed Forms',
				icon: FileCheck
			}
		},
		{
			path: 'admin/employee-documents',
			name: 'employee-documents',
			component: AdminEmployeeDocumentsView,
			meta: {
				requiresAuth: true,
				roles: ['admin'],
				title: 'Employee Documents',
				icon: FileText
			}
		},
		// Employee Routes
		{
			path: 'employee-dashboard',
			name: 'employee-dashboard',
			component: AdminEmployeeDashboardView,
			meta: {
				requiresAuth: true,
				title: 'Dashboard',
				icon: LayoutDashboard,
				roles: ['employee']
			}
		},
		{
			path: 'employee-materials',
			name: 'employee-materials',
			component: EmployeeMaterialsView,
			meta: {
				requiresAuth: true,
				roles: ['employee'],
				title: 'Available Materials',
				icon: Folders
			}
		},
		{
			path: 'employee/completed-forms',
			name: 'employee-completed-forms',
			component: EmployeeCompletedFormsView,
			meta: {
				requiresAuth: true,
				roles: ['employee'],
				title: 'My Completed Forms',
				icon: FileCheck
			}
		},
		{
			path: 'employee/documents',
			name: 'my-documents',
			component: EmployeeDocumentsView,
			meta: {
				requiresAuth: true,
				roles: ['employee'],
				title: 'My Documents',
				icon: FileText
			}
		},
		// Shared Protected
		{
			path: 'profile',
			name: 'profile',
			component: ProfileView,
			meta: {
				requiresAuth: true,
				title: 'Profile',
				roles: ['employee', 'admin'],
				hidden: true
			}
		},
		]
	},
	{
		path: '/login',
		name: 'login',
		component: LoginView,
		meta: {
			requiresAuth: false,
			title: 'Login'
		}
	},
	{
		path: '/register',
		name: 'register',
		component: RegistrationView,
		meta: { requiresAuth: false, title: 'Register' }
	},
	{
		path: '/forgot-password',
		name: 'ForgotPassword',
		component: ForgotPasswordView,
		meta: { requiresAuth: false, title: 'Forgot Password' }
	},
	{
		path: '/500',
		name: 'server-error',
		component: () => import('@/views/ServerErrorView.vue'),
		meta: { requiresAuth: false }
	},
	{
		path: '/:pathMatch(.*)*',
		name: 'not-found',
		component: NotFoundView,
		meta: {
			requiresAuth: false,
			title: 'Not Found'
		}
	}
]

// Router Instance
const router = createRouter({
	history: createWebHistory(process.env.VUE_APP_BASE_URL),
	routes
})

// Navigation Helper
export const getNavigationItems = (routes: RouteRecordRaw[]): NavigationItem[] => {
	const authStore = useAuthStore()
	const userRole = authStore.user?.role?.toLowerCase()
	const layoutRoute = routes.find(route => route.path === '/')
	if (!layoutRoute?.children) return []

	return layoutRoute.children
		.filter(route => {
			const meta = route.meta as RouteMeta | undefined
			const routeRoles = (meta?.roles as string[] | undefined) ?? []
			
			return (
				meta?.requiresAuth &&
				!meta?.hidden &&
				route.name !== 'not-found' &&
				(routeRoles.length === 0 || routeRoles.includes(userRole ?? ''))
			)
		})
		.map(route => ({
			name: (route.meta as RouteMeta)?.title || String(route.name),
			to: `/${String(route.path).replace(/^\/+/, '')}`,
			icon: (route.meta as RouteMeta)?.icon
		}))
}

router.beforeEach(async (to, from, next) => {
	const authStore = useAuthStore()
	const requiresAuth = to.matched.some(r => r.meta.requiresAuth)

	if (!authStore.isInitialized || to.name === 'login') {
		await authStore.checkAuth()
	}

	const isAuthed = authStore.isAuthenticated
	const userRole = authStore.user?.role?.toLowerCase() ?? ''
	const allowedRoles = ((to.meta.roles as string[] | undefined) ?? []).map(r => r.toLowerCase())

	if (to.name === 'login' && isAuthed) {
		if (userRole === 'admin') return next({ name: 'admin-dashboard' })
		if (userRole === 'employee') return next({ name: 'employee-dashboard' })
		return next({ name: 'portal-home' })
	}

	if (requiresAuth && !isAuthed) {
		const redirectPath = to.path === '/' ? undefined : to.fullPath
		return next({ name: 'login', query: redirectPath ? { redirect: redirectPath } : undefined })
	}

	if (requiresAuth && isAuthed && !userRole) {
		return next()
	}

	if (requiresAuth && allowedRoles.length && !allowedRoles.includes(userRole)) {
		if (userRole === 'admin') return next({ name: 'admin-dashboard' })
		if (userRole === 'employee') return next({ name: 'employee-dashboard' })
		return next({ name: 'login' })
	}

	if (requiresAuth && to.name !== 'portal-home') {
		saveLastAccessibleRoute(to.fullPath)
	}
	
	next()
})

export default router
